import { Spinner } from "@npco/zeller-design-system";
import * as Illustration from "@npco/zeller-design-system/illustration";
import { ADT } from "pb.adt";

import { formatCurrency } from "../../../../@lib-i18n/format-currency";
import { Layout } from "../../shared/layout";
import { createOperationPages } from "../../shared/operation-pages";
import { PageTemplate } from "../../shared/page-template";
import { Prompt } from "../../shared/prompt";
import { Signature } from "../../shared/signature";
import { Page } from "./page";

const defaultLayoutProps = {
	actionsStyle: "full-width",
	logo: true,
} satisfies Layout.Props;

export function render(page: Page) {
	return ADT.switch(page, {
		...createOperationPages("Payment"),
		InitialisingPayment: PageTemplate({
			image: <Spinner size="lg" />,
			title: "Initialising Payment",
			intermediate: true,
		}),
		AwaitTipEntry: ({ onClose, onSkip, cancelling }) => (
			<Layout
				{...defaultLayoutProps}
				onClose={onClose}
				pending={cancelling ? "close" : undefined}
				actions={[{ label: "Skip", onClick: onSkip }]}
			>
				<Prompt
					image={<Illustration.TerminalT2Tip />}
					title="Waiting for Tip"
				/>
			</Layout>
		),
		SignatureSubmitted: ({
			signature,
			onClose,
			onApprove,
			onDecline,
			cancelling,
		}) => (
			<Layout
				{...defaultLayoutProps}
				onClose={onClose}
				pending={cancelling ? "close" : undefined}
				actions={[
					{ label: "Approve", onClick: onApprove, primary: true },
					{ label: "Decline", onClick: onDecline },
				]}
			>
				<Prompt
					image={<Signature signature={signature} />}
					title="Verify Signature"
				/>
			</Layout>
		),
		TipProvided: ({ message = "Tip entered", onClose, cancelling }) => {
			const parts = message.split("\n");
			return (
				<Layout
					{...defaultLayoutProps}
					onClose={onClose}
					pending={cancelling ? "close" : undefined}
				>
					<Prompt
						image={<Illustration.TerminalT2Tip loop={false} />}
						/* v8 ignore next -- String.split always yields a defined element at index 0, so the fallback is unreachable */
						title={parts[0] ?? "Tip entered"}
						datapoint={parts[1]}
					/>
				</Layout>
			);
		},
		// response states
		Approved: ({ currency, totalCents, surchargeCents, tipCents, onDone }) => (
			<Layout
				{...defaultLayoutProps}
				timeout={{ onTimeout: onDone }}
				actions={[{ label: "Done", onClick: onDone, primary: true }]}
			>
				<Prompt
					image={<Spinner size="lg" result="ok" />}
					title={`${formatCurrency({ amountInCents: totalCents, currency })} Approved`}
					message={[
						...(surchargeCents
							? [
									`Surcharge: ${formatCurrency({ amountInCents: surchargeCents, currency })}`,
								]
							: []),
						...(tipCents
							? [
									`Tip: ${formatCurrency({ amountInCents: tipCents, currency })}`,
								]
							: []),
					]}
				/>
			</Layout>
		),
		Declined: ({ title, subtitle, message, onRetry, onCancel }) => (
			<Layout
				{...defaultLayoutProps}
				timeout={{ onTimeout: onCancel }}
				actions={[
					{ label: "Try Again", onClick: onRetry, primary: true },
					{ label: "Cancel", onClick: onCancel },
				]}
			>
				<Prompt
					image={<Spinner size="lg" result="error" />}
					title={title}
					subtitle={subtitle}
					message={message}
				/>
			</Layout>
		),
	});
}
